import React, { useState } from 'react'

import Button from '../../common/Button/Button.component'
import InputField from '../../common/InputField/InputField.component'
import Label from '../../common/Label/Label'
import RadioQuestion from '../../ProfilePage/common/RadioQuestion'
import SectionRow from '../../common/SectionRow'
import StringConstants from '../../../constants/StringConstants'
import ValidationUtils from '../../../utils/validation/ValidationUtils'
import YearMonthDaySelector from '../../common/Select/YearMonthDaySelector'
import { isEmpty } from 'lodash'
import styled from '@emotion/styled'
import theme from '../../../global/theme'

interface ModelProps {
  loading: boolean
  onConfirm: (
    date: string,
    gender: string,
    fName: string,
    lName: string
  ) => void
  onReject: () => void
}

const Container = styled.div`
  h3 {
    color: #272f69;
    padding-bottom: 15px;
  }
  p {
    padding-bottom: 15px;
    font-weight: 600;
    font-size: 14px;
    color: ${theme.colors.textGrey};
  }

  .confirmation_buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
  }
  .find-my-medicare {
    grid-template-columns: 1fr 2fr;
  }

  @media screen and (max-width: 600px) {
    .header {
      max-width: 80%;
    }
  }
`
const ModalButton = styled.div`
  width: 100%;
  text-decoration: none;
`
const Question = styled.div``

const AddSpouseConfirmationModal: React.FC<ModelProps> = ({
  loading,
  onConfirm,
  onReject,
}) => {
  const [year, setYear] = useState<number | ''>('')
  const [month, setMonth] = useState<number | ''>('')
  const [day, setDay] = useState<number | ''>('')
  const [errors, setErrors] = useState({ year: '', month: '', day: '' })
  const [firstName, setFirstName] = React.useState('')
  const [firstNameError, setFirstNameError] = React.useState(false)
  const [firstNameHelperText, setFirstNameHelperText] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [lastNameError, setLastNameError] = React.useState(false)
  const [lastNameHelperText, setLastNameHelperText] = React.useState('')
  const [genderError, setGenderError] = React.useState<string>('')

  const [showConfirmationContent, setShowConfirmationContent] =
    React.useState<boolean>(false)

  const [gender, setGender] = useState<'male' | 'female' | ''>('')

  const handleOnOk = () => {
    setShowConfirmationContent(true)
  }

  const validateDate = () => {
    const newErrors = { year: '', month: '', day: '' }

    if (year === '') newErrors.year = 'Required'
    if (month === '') newErrors.month = 'Required'
    if (day === '') newErrors.day = 'Required'

    if (year && month && day) {
      const date = new Date(year, month - 1, day)
      if (
        date.getFullYear() !== year ||
        date.getMonth() + 1 !== month ||
        date.getDate() !== day
      ) {
        newErrors.day = 'Invalid date'
      }
    }

    setErrors(newErrors)

    return Object.values(newErrors).every((error) => error === '')
  }

  const validateFirstName = (firstName: string) => {
    const firstNameError = !ValidationUtils.validateFirstName(firstName)
    setFirstNameError(firstNameError)
    setFirstNameHelperText(
      isEmpty(firstName)
        ? StringConstants.UI_ERROR_MSG_FOR_EMPTY_FIRST_NAME
        : firstNameError
        ? StringConstants.UI_ERROR_MSG_FOR_INVALID_FIRST_NAME
        : ''
    )
  }
  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateFirstName(e.target.value)
    setFirstName(e.target.value)
  }

  const validateLastName = (lastName: string) => {
    const lastNameError = !ValidationUtils.validateLastName(lastName)
    setLastNameError(lastNameError)
    setLastNameHelperText(
      isEmpty(lastName)
        ? StringConstants.UI_ERROR_MSG_FOR_EMPTY_LAST_NAME
        : lastNameError
        ? StringConstants.UI_ERROR_MSG_FOR_INVALID_LAST_NAME
        : ''
    )
  }
  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateLastName(e.target.value)
    setLastName(e.target.value)
  }

  const handleResponseSelection = (id: any, responseArray: any) => {
    setGender(responseArray[0])
  }

  const validateGender = () => {
    if (isEmpty(gender)) {
      setGenderError('Required')
    }
  }
  const handleSubmit = () => {
    validateFirstName(firstName)
    validateLastName(lastName)
    validateGender()
    if (
      validateDate() &&
      !isEmpty(firstName) &&
      !isEmpty(lastName) &&
      !isEmpty(gender)
    ) {
      onConfirm(
        `${year}-${month.toString().padStart(2, '0')}-${day
          .toString()
          .padStart(2, '0')}`,
        gender.toLowerCase(),
        firstName,
        lastName
      )
    }
  }

  return (
    <Container>
      <div className='header'>
        <h3>Would You Like to Add a Spouse?</h3>
        <p>
          Household discounts up to 15% may be available for each member. Would
          you like to view rates for a Spouse/Partner?
        </p>
      </div>
      {!showConfirmationContent ? (
        <div className='confirmation_buttons'>
          <ModalButton>
            <Button
              color='primary'
              bold={false}
              width='100%'
              onClick={(e) => {
                onReject()
              }}
              fontSize='14px'
            >
              No
            </Button>
          </ModalButton>
          <ModalButton>
            <Button
              variant='contained'
              bold={false}
              width='100%'
              color='primary'
              onClick={(e) => {
                handleOnOk()
              }}
              fontSize='14px'
            >
              Yes
            </Button>
          </ModalButton>
        </div>
      ) : (
        <>
          <SectionRow>
            <InputField
              label='Your Spouse/Partner’s first name'
              isRequired
              error={firstNameError}
              helperText={firstNameHelperText}
              value={firstName}
              placeholder='First name'
              width='100%'
              color='primary'
              onChange={handleFirstNameChange}
            />
          </SectionRow>

          <SectionRow>
            <InputField
              label="Your Spouse/Partner's last name"
              isRequired
              error={lastNameError}
              helperText={lastNameHelperText}
              value={lastName}
              placeholder='Last name'
              width='100%'
              color='primary'
              onChange={handleLastNameChange}
            />
          </SectionRow>

          <SectionRow>
            <YearMonthDaySelector
              isRequired
              label='Spouse/Partner Date of Birth'
              onYearChange={setYear}
              onMonthChange={setMonth}
              onDayChange={setDay}
              errors={errors}
              setErrors={setErrors}
              loading={loading}
            ></YearMonthDaySelector>
          </SectionRow>

          {/* <FormHelperText error>{errors.year}</FormHelperText>
          <FormHelperText error>{errors.month}</FormHelperText>
          <FormHelperText error>{errors.day}</FormHelperText> */}

          <SectionRow>
            <RadioQuestion
              row
              squared={false}
              handleResponseSelection={handleResponseSelection}
              id={Math.random()}
              isRequired={true}
              options={[{ option: 'Male' }, { option: 'Female' }]}
              question={'Spouse/Partner Gender'}
              value={gender}
              loading={loading}
              error={isEmpty(genderError) ? false : true}
            ></RadioQuestion>
          </SectionRow>

          <div className='confirmation_buttons find-my-medicare'>
            <ModalButton>
              <Button
                color='primary'
                bold={false}
                width='100%'
                onClick={(e) => {
                  onReject()
                }}
                fontSize='14px'
              >
                No
              </Button>
            </ModalButton>
            <ModalButton>
              <Button
                variant='contained'
                bold={false}
                width='100%'
                color='secondary'
                onClick={(e) => {
                  handleSubmit()
                }}
                fontSize='14px'
                loading={loading}
              >
                Find My Medicare Plans
              </Button>
            </ModalButton>
          </div>
        </>
      )}
    </Container>
  )
}

export default AddSpouseConfirmationModal
